import { createUniqueElementId } from 'app.util';
import { defaults } from 'es-toolkit/compat';
import { type ChangeEvent, type FC, type FocusEvent, type FocusEventHandler, type ReactNode, useCallback, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import { Field } from 'react-final-form';
import { stylesheet } from 'typestyle';
import { FormLabelWithOptional } from './FormLabelWithOptional';
import type { CommonFormInputProps, FormGroupProps, TextLikeInputProps } from './form.types';

export interface JFormInputProps extends TextLikeInputProps, CommonFormInputProps, FormGroupProps {
  // default = "text"
  defaultable?: boolean;
  type?: string;
  as?: 'input' | 'textarea';
  min?: string | number;
  max?: string | number;
  step?: string | number;
  list?: string;
  warningMessage?: string;
  warningColor?: string;
  onChange?: (value: string) => void;
  labelStyle?: string;
  ariaLabel?: string;
  number?: boolean; // if you want the input to be formatted/parsed as a number
  /** Used to show "X or fewer characters recommended. Count: Y */
  valueAwareDescription?: (props: { value?: string | number }) => ReactNode;
}

const css = stylesheet({
  warningMessage: {
    color: '#d43900',
  },
});

const parseInputValueAsNumber = (inputValue: string) => {
  if (inputValue === '') return null;
  return +inputValue;
};

export const JFormInput: FC<JFormInputProps> = ({
  label,
  ariaLabel,
  validate,
  description,
  defaultable,
  optional,
  className,
  labelStyle,
  onBlur,
  onChange,
  parse,
  name,
  number,
  warningMessage,
  valueAwareDescription,
  ...inputProps
}) => {
  const inputId = useMemo(() => createUniqueElementId('text-input'), []);
  const defaultInputProps = defaults(inputProps, {
    autoComplete: 'off',
  });
  const parseValue = useCallback(
    (value: string | number, finalFormName = '') => {
      if (parse) return parse(value, finalFormName || '');
      if (number) return parseInputValueAsNumber(value?.toString());
      return value;
    },
    [
      number,
      parse,
    ],
  );

  const handleBlur = useCallback(
    (focusEvent: FocusEvent<HTMLInputElement>, finalFormFieldBlur: FocusEventHandler<HTMLInputElement>) => {
      // If the parent component needs to override the onBlur behavior, it can do so here
      // If onBlur is not overridden, use the final form field's onBlur method
      onBlur ? onBlur(focusEvent) : finalFormFieldBlur(focusEvent);
    },
    [
      onBlur,
    ],
  );

  return (
    <Form.Group className={className} controlId={inputId}>
      <FormLabelWithOptional
        className={labelStyle}
        htmlFor={inputId}
        label={label && (typeof label === 'string' ? label : label())}
        optional={optional}
      />
      <Field format={(item) => item?.toString()} name={name} parse={parseValue} validate={validate}>
        {(fieldProps) => (
          <>
            <Form.Control
              aria-label={ariaLabel}
              data-cy={`input-${name}`}
              isInvalid={fieldProps.meta.invalid}
              {...defaultInputProps}
              {...fieldProps.input}
              disabled={inputProps.disabled}
              onBlur={(event: FocusEvent<HTMLInputElement>) => handleBlur(event, fieldProps.input.onBlur)}
              onChange={(event: ChangeEvent<HTMLSelectElement>) => {
                fieldProps.input.onChange(event);
                onChange?.(event.target.value);
              }}
              value={defaultable ? (fieldProps.input.value ?? '') : fieldProps.input.value}
            />
            {fieldProps.meta.invalid && (
              <Form.Control.Feedback data-cy={`error-${name}`} type="invalid">
                {fieldProps.meta.error}
              </Form.Control.Feedback>
            )}
            {valueAwareDescription
              ? valueAwareDescription({
                  value: fieldProps.input.value,
                })
              : null}
          </>
        )}
      </Field>
      {description && (
        <Form.Text id={`${inputId}-description`} muted>
          {typeof description === 'string' ? description : description()}
        </Form.Text>
      )}
      {warningMessage && (
        <Form.Text className={css.warningMessage} id={`${inputId}-warning`}>
          {warningMessage}
        </Form.Text>
      )}
    </Form.Group>
  );
};
